import { Camera, LoaderCircle, UserRound } from 'lucide-react';
import { useRef, type ChangeEvent } from 'react';

/**
 * Circular profile photo, falling back to initials (then a generic person icon) when none has
 * been uploaded yet. `editable` adds a small camera button that opens a file picker and hands the
 * chosen file to `onSelectFile` -- the caller owns the actual upload request and cache refresh,
 * since that differs between the applicant's own `/me/avatar` and staff acting on `/applicants/:id/avatar`.
 */
export function Avatar({
  photoUrl,
  initials,
  size = 56,
  editable = false,
  uploading = false,
  uploadLabel = 'Upload profile photo',
  onSelectFile,
}: {
  photoUrl: string | null;
  initials: string;
  size?: number;
  editable?: boolean;
  uploading?: boolean;
  uploadLabel?: string;
  onSelectFile?: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      <span
        className="grid size-full place-items-center overflow-hidden rounded-full bg-burgundy-soft text-lg font-bold text-burgundy"
        aria-hidden="true"
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="size-full object-cover" />
        ) : (
          initials || <UserRound className="size-6" />
        )}
      </span>
      {editable && (
        <>
          <button
            type="button"
            className="group absolute -right-0.5 -bottom-0.5 grid size-11 place-items-end justify-items-end disabled:opacity-60"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            aria-label={uploadLabel}
          >
            {/* The visible badge stays a small circle proportional to the avatar; this button's
                own box extends past it (unclipped, since nothing here clips overflow) so the tap
                target reaches the ~44px minimum without the badge itself looking oversized. */}
            <span className="grid size-6 place-items-center rounded-full border-2 border-surface bg-ink text-surface shadow-sm transition group-hover:bg-ink/85">
              {uploading ? (
                <LoaderCircle className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Camera className="size-3.5" aria-hidden="true" />
              )}
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file && onSelectFile) onSelectFile(file);
            }}
          />
        </>
      )}
    </span>
  );
}
